/**
 * InsightsPanel V2 - Evidence-First Market Insights
 * Renders insight cards with real DataForSEO keyword data.
 * 
 * NEW: Part of two-panel flow. Remains locked until seeds are confirmed
 * from KeywordSetupPanel.
 */

import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, RefreshCw, ChevronDown, ChevronUp, AlertCircle, 
  Check, Loader2, TrendingUp, DollarSign, Target, Calendar,
  Lock, Settings2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format volume for display
function formatVolume(vol) {
  if (!vol) return '0';
  if (vol >= 1000000) return `${(vol/1000000).toFixed(1)}M`;
  if (vol >= 1000) return `${(vol/1000).toFixed(1)}k`;
  return String(vol);
}

// Confidence badge colors
function getConfidenceClass(conf) {
  if (conf >= 0.75) return 'bg-green-100 text-green-700 border-green-200';
  if (conf >= 0.5) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

// Intent badge colors
function getIntentClass(intent) {
  switch (intent) {
    case 'transactional': return 'bg-green-50 text-green-600';
    case 'commercial': return 'bg-blue-50 text-blue-600';
    case 'informational': return 'bg-purple-50 text-purple-600';
    default: return 'bg-slate-50 text-slate-500';
  }
}

// Cluster type icons
function getClusterIcon(type) {
  switch (type) {
    case 'local': return '📍';
    case 'trust': return '⭐';
    case 'price': return '💰';
    case 'comparison': return '⚖️';
    case 'segment': return '👥';
    case 'demo': return '🎯';
    case 'seasonality': return '📅';
    case 'high_value': return '💎';
    default: return '📊';
  }
}

function EvidenceKeyword({ kw }) {
  return (
    <div className="flex items-start gap-2 py-1.5 px-2 bg-slate-50 rounded text-xs font-mono">
      <span className="flex-1 text-slate-800 font-medium">"{kw.keyword}"</span>
      <div className="flex items-center gap-3 text-slate-500 shrink-0">
        <span className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          {formatVolume(kw.search_volume)}/mo
        </span>
        {kw.cpc > 0 && (
          <span className="flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            ${kw.cpc.toFixed(2)}
          </span>
        )}
        {kw.intent && kw.intent !== 'mixed' && (
          <span className={`px-1.5 py-0.5 rounded text-xs ${getIntentClass(kw.intent)}`}>
            {kw.intent}
          </span>
        )}
        {kw.seasonality_peak && (
          <span className="flex items-center gap-1 text-amber-600">
            <Calendar className="w-3 h-3" />
            {kw.seasonality_peak}
          </span>
        )}
      </div>
    </div>
  );
}

function InsightCard({ insight, expanded, onToggle }) {
  const conf = insight.confidence || 0;
  const confClass = getConfidenceClass(conf);
  const clusterIcon = getClusterIcon(insight.cluster_type);
  
  // Use why_it_matters if available, otherwise fall back to takeaway
  const summary = insight.why_it_matters || insight.takeaway || '';
  
  // Use evidence_keywords if available (new format), otherwise parse evidence strings
  const evidenceKeywords = insight.evidence_keywords || [];
  const evidenceStrings = insight.evidence || [];
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors">
      <button 
        onClick={onToggle} 
        className="w-full flex items-start gap-3 p-3 hover:bg-slate-50 text-left"
        data-testid={`insight-card-${insight.id}`}
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shrink-0 text-lg">
          {clusterIcon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-sm text-slate-800">{insight.title}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${confClass}`}>
              {Math.round(conf * 100)}%
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{summary}</p>
          
          {/* Quick stats row */}
          {(insight.total_volume || insight.median_cpc) && (
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              {insight.total_volume > 0 && (
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {formatVolume(insight.total_volume)} total vol
                </span>
              )}
              {insight.median_cpc > 0 && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  ${insight.median_cpc.toFixed(2)} avg CPC
                </span>
              )}
              {insight.keyword_count > 0 && (
                <span>{insight.keyword_count} keywords</span>
              )}
            </div>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400 mt-1" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400 mt-1" />
        )}
      </button>
      
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-100">
          {/* Evidence section with keyword metrics */}
          {(evidenceKeywords.length > 0 || evidenceStrings.length > 0) && (
            <div className="pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Evidence</p>
              </div>
              <div className="space-y-1">
                {evidenceKeywords.length > 0 ? (
                  // New format with structured keyword data
                  evidenceKeywords.slice(0, 6).map((kw, i) => (
                    <EvidenceKeyword key={i} kw={kw} />
                  ))
                ) : (
                  // Fallback to string evidence
                  evidenceStrings.slice(0, 6).map((item, i) => (
                    <div key={i} className="text-xs text-slate-600 font-mono py-1 px-2 bg-slate-50 rounded">
                      • {item}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          {/* Action section */}
          {insight.action && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded bg-blue-500 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-800 mb-1">Recommended Action</p>
                  <p className="text-xs text-blue-700 leading-relaxed">{insight.action}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Tags */}
          {insight.tags && insight.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {insight.tags.slice(0, 5).map((tag, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function InsightsPanel({ 
  businessAnalysis, 
  qualifiers, 
  channelFocus, 
  websiteId, 
  isQualifiersComplete,
  // New props for two-panel flow
  confirmedSeeds = null,
  isSeedsConfirmed = false,
  onEditSeeds
}) {
  const [expanded, setExpanded] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [insights, setInsights] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [fetchAttempted, setFetchAttempted] = useState(false);
  
  // Auto-fetch insights when seeds are confirmed (only once)
  useEffect(() => {
    if (isSeedsConfirmed && confirmedSeeds && confirmedSeeds.length > 0 && !loaded && !loading && !fetchAttempted) {
      setFetchAttempted(true);
      fetchInsights(false);
    }
  }, [isSeedsConfirmed, confirmedSeeds]);
  
  // Reset fetch state when seeds change
  useEffect(() => {
    if (!isSeedsConfirmed) {
      setFetchAttempted(false);
      setLoaded(false);
      setInsights([]);
      setError(null);
    }
  }, [isSeedsConfirmed]);
  
  async function fetchInsights(force = false) {
    if (!businessAnalysis || !qualifiers) return;
    if (!isSeedsConfirmed || !confirmedSeeds || confirmedSeeds.length === 0) {
      toast.error('Please confirm seed keywords first');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      // Use AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 second timeout
      
      // Use new /generate endpoint with confirmed seeds
      const res = await fetch(`${API_URL}/api/insights/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          businessAnalysis,
          qualifiers,
          channelFocus: channelFocus || 'search',
          market: { country: 'US', language: 'en' },
          websiteId,
          forceRefresh: force,
          confirmedSeeds
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        if (res.status === 520 || res.status === 504 || res.status === 502) {
          throw new Error('Server timeout - please try refreshing');
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      
      setInsights(data.insightCards || []);
      setMeta(data.meta || {});
      setLoaded(true);
      
      // Auto-expand first card
      if (data.insightCards?.length > 0) {
        setExpandedCards({ [data.insightCards[0].id]: true });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out - please try refreshing');
        toast.error('Request timed out - please click Refresh to try again');
      } else {
        setError(err.message);
        toast.error('Failed to load market insights');
      }
    } finally {
      setLoading(false);
    }
  }
  
  function toggleCard(id) {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  }
  
  function handleConfirm() {
    setConfirmed(true);
    setExpanded(false);
    toast.success('Insights confirmed');
  }
  
  // Don't render if qualifiers aren't complete
  if (!isQualifiersComplete) return null;
  
  // Locked state - seeds not yet confirmed
  if (!isSeedsConfirmed) {
    return (
      <div 
        className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm opacity-75"
        data-testid="insights-panel-locked"
      >
        <div className="flex items-center justify-between p-4 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
              <Lock className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-500">Market Insights</h3>
              <p className="text-xs text-slate-400">
                Confirm seed keywords above to unlock
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Settings2 className="w-4 h-4" />
            <span>Setup required</span>
          </div>
        </div>
      </div>
    );
  }
  
  const source = meta.source || 'unknown';
  const keywordCount = meta.keywordCount || 0;
  const fromCache = meta.fromCache || false;
  
  return (
    <div 
      className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" 
      data-testid="insights-panel"
    >
      {/* Header */}
      <div 
        className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
          confirmed ? 'bg-gradient-to-r from-blue-50 to-blue-100' : 'hover:bg-slate-50'
        }`} 
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            confirmed ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-blue-100 to-blue-200'
          }`}>
            <Lightbulb className={`w-5 h-5 ${confirmed ? 'text-white' : 'text-blue-600'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800">Market Insights</h3>
              {confirmed && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1 border border-green-200">
                  <Check className="w-3 h-3" />Confirmed
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              {insights.length > 0 
                ? `${insights.length} insights • ${keywordCount} keywords analyzed`
                : 'DataForSEO analysis'
              }
              {fromCache && <span className="text-slate-400"> • cached</span>}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </div>
      
      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Loading state */}
          {loading && (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">Analyzing market data...</p>
              <p className="text-xs text-slate-400 mt-1">Fetching keywords from DataForSEO</p>
            </div>
          )}
          
          {/* Error state */}
          {error && !loading && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-center gap-2 text-red-700 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-semibold">Failed to load insights</span>
              </div>
              <p className="text-xs text-red-600 mb-3">{error}</p>
              <Button size="sm" variant="outline" onClick={() => fetchInsights(true)}>
                <RefreshCw className="w-3 h-3 mr-1.5" />Retry
              </Button>
            </div>
          )}
          
          {/* Insights list */}
          {!loading && !error && insights.length > 0 && (
            <div className="space-y-2">
              {insights.map(ins => (
                <InsightCard 
                  key={ins.id} 
                  insight={ins} 
                  expanded={expandedCards[ins.id]} 
                  onToggle={() => toggleCard(ins.id)} 
                />
              ))}
            </div>
          )}
          
          {/* Empty state */}
          {!loading && !error && insights.length === 0 && loaded && (
            <div className="py-8 text-center">
              <Lightbulb className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">No insights available</p>
              <p className="text-xs text-slate-400 mt-1">Try refreshing or adjusting your qualifiers</p>
            </div>
          )}
          
          {/* Footer with actions */}
          {!loading && insights.length > 0 && (
            <>
              <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="text-xs text-slate-400">
                    {source === 'dataforseo' && (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                        DataForSEO data
                      </span>
                    )}
                    {source === 'llm_fallback' && (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        AI-generated
                      </span>
                    )}
                  </div>
                  
                  {/* Edit seeds link */}
                  {onEditSeeds && (
                    <button
                      onClick={onEditSeeds}
                      className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                      data-testid="edit-seeds-link"
                    >
                      <Settings2 className="w-3 h-3" />
                      Edit seeds
                    </button>
                  )}
                </div>
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => fetchInsights(true)} 
                  disabled={loading}
                >
                  <RefreshCw className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              {!confirmed && (
                <Button 
                  onClick={handleConfirm} 
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Insights
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
